import type { ReactNode } from 'react';

// Project timeline data for different types of welding projects
export interface TimelineStep {
  title: string;
  duration: string; // e.g., "1-2 days", "1 week"
  description: string;
  icon?: ReactNode; // Will be populated in the component
}

export interface ProjectTimeline {
  id: string;
  title: string;
  totalDuration: string;
  description: string;
  steps: TimelineStep[];
}

export const projectTimelines: ProjectTimeline[] = [
  {
    id: "cable-railing",
    title: "Cable Railing",
    totalDuration: "2-3 weeks",
    description: "Modern, minimalist cable railing systems that offer unobstructed views and contemporary style.",
    steps: [
      {
        title: "Initial Consultation",
        duration: "1-2 days",
        description: "Discuss your space and design preferences, take measurements, and determine the optimal cable system for your needs."
      },
      {
        title: "Design & Approval",
        duration: "3-5 days",
        description: "Develop detailed plans showing cable spacing, post locations, and mounting hardware for your approval."
      },
      {
        title: "Material Procurement",
        duration: "2-7 days",
        description: "Source high-grade stainless steel cables, tensioners, and custom framework components."
      },
      {
        title: "Framework Fabrication",
        duration: "5-8 days",
        description: "Craft the metal posts, handrails, and mounting brackets in our precision workshop."
      },
      {
        title: "Installation",
        duration: "1-3 days",
        description: "Professional installation with precise tensioning to ensure safety and aesthetic perfection."
      },
      {
        title: "Final Inspection",
        duration: "1 day",
        description: "Verify proper tension, stability, and compliance with all building codes and regulations."
      }
    ]
  },
  {
    id: "indoor-railing",
    title: "Indoor Railing",
    totalDuration: "2-4 weeks",
    description: "Custom interior railings that blend seamlessly with your home's décor and enhance indoor spaces.",
    steps: [
      {
        title: "Design Consultation",
        duration: "1-2 days",
        description: "Explore design options that complement your interior aesthetic while meeting safety requirements."
      },
      {
        title: "Material Selection",
        duration: "2-3 days",
        description: "Choose from our selection of premium metals, finishes, glass panels, and wood components."
      },
      {
        title: "Detailed Measurements",
        duration: "1 day",
        description: "Take precise on-site measurements to ensure perfect fit in your indoor space."
      },
      {
        title: "Design Rendering",
        duration: "3-5 days",
        description: "Create detailed 3D renderings for your approval before fabrication begins."
      },
      {
        title: "Custom Fabrication",
        duration: "7-12 days",
        description: "Handcraft your indoor railing system with meticulous attention to detail and fine finishes."
      },
      {
        title: "Installation",
        duration: "1-2 days",
        description: "Expert installation with minimal disruption to your home environment."
      },
      {
        title: "Final Walkthrough",
        duration: "1 day",
        description: "Inspect the completed work together to ensure your complete satisfaction."
      }
    ]
  },
  {
    id: "ornamental-work",
    title: "Ornamental Work",
    totalDuration: "3-6 weeks",
    description: "Exquisite ornamental metal work that showcases traditional craftsmanship and artistic detail.",
    steps: [
      {
        title: "Artistic Consultation",
        duration: "1-3 days",
        description: "Discuss your vision for ornamental elements, historical styles, and artistic preferences."
      },
      {
        title: "Design Development",
        duration: "5-10 days",
        description: "Create detailed sketches and patterns of ornamental elements for your approval."
      },
      {
        title: "Material Selection",
        duration: "2-3 days",
        description: "Choose appropriate metals based on the ornamental style, durability requirements, and finish options."
      },
      {
        title: "Pattern Making",
        duration: "3-7 days",
        description: "Develop precise patterns and templates for each ornamental element."
      },
      {
        title: "Metal Forming",
        duration: "7-14 days",
        description: "Skillfully forge, shape, and craft each ornamental component by hand using traditional techniques."
      },
      {
        title: "Assembly & Welding",
        duration: "3-7 days",
        description: "Combine individual elements into the complete ornamental piece with seamless connections."
      },
      {
        title: "Finishing Process",
        duration: "3-5 days",
        description: "Apply patinas, polishes, or protective coatings to enhance and preserve the ornamental details."
      },
      {
        title: "Installation",
        duration: "1-3 days",
        description: "Carefully install your custom ornamental work in its designated location."
      }
    ]
  },
  {
    id: "outdoor-railing",
    title: "Outdoor Railing",
    totalDuration: "2-4 weeks",
    description: "Durable, weather-resistant railings for decks, patios, and exterior staircases.",
    steps: [
      {
        title: "Site Evaluation",
        duration: "1-2 days",
        description: "Assess your outdoor space, environmental factors, and safety requirements."
      },
      {
        title: "Material Recommendation",
        duration: "1-2 days",
        description: "Suggest appropriate weather-resistant metals and finishes based on your location's climate."
      },
      {
        title: "Design Planning",
        duration: "3-5 days",
        description: "Create outdoor railing designs that complement your home's exterior architecture."
      },
      {
        title: "Material Procurement",
        duration: "3-7 days",
        description: "Source high-quality metals and components engineered to withstand outdoor conditions."
      },
      {
        title: "Fabrication",
        duration: "5-10 days",
        description: "Construct your outdoor railing system with corrosion-resistant welding and durable finishes."
      },
      {
        title: "Surface Treatment",
        duration: "2-4 days",
        description: "Apply weatherproof coatings, galvanization, or powder coating for long-term protection."
      },
      {
        title: "Installation",
        duration: "1-3 days",
        description: "Securely mount your outdoor railing with proper anchoring to withstand environmental forces."
      },
      {
        title: "Final Inspection",
        duration: "1 day",
        description: "Ensure all outdoor safety standards are met and the system is properly sealed against the elements."
      }
    ]
  },
  {
    id: "spiral-stairs",
    title: "Spiral Stairs",
    totalDuration: "4-8 weeks",
    description: "Space-efficient, visually stunning spiral staircases custom-built for your space.",
    steps: [
      {
        title: "Space Assessment",
        duration: "1-2 days",
        description: "Measure your available space and determine the optimal spiral staircase dimensions."
      },
      {
        title: "Design Consultation",
        duration: "2-3 days",
        description: "Discuss tread designs, railing options, and central column styles for your spiral staircase."
      },
      {
        title: "Engineering & Planning",
        duration: "5-10 days",
        description: "Create detailed technical drawings and ensure structural calculations meet all building codes."
      },
      {
        title: "Material Selection",
        duration: "2-3 days",
        description: "Choose metal types, tread materials, and finishing options for your custom spiral staircase."
      },
      {
        title: "Center Column Fabrication",
        duration: "5-7 days",
        description: "Craft the precision-engineered central support column that forms the staircase's backbone."
      },
      {
        title: "Tread & Railing Fabrication",
        duration: "7-14 days",
        description: "Manufacture each individual tread and railing component with exact specifications."
      },
      {
        title: "Quality Testing",
        duration: "2-3 days",
        description: "Test-assemble key components in our workshop to verify precise fit and structural integrity."
      },
      {
        title: "On-site Installation",
        duration: "2-4 days",
        description: "Expert installation of your spiral staircase with minimal disruption to your space."
      },
      {
        title: "Final Safety Inspection",
        duration: "1 day",
        description: "Comprehensive verification of all safety features and structural stability."
      }
    ]
  },
  {
    id: "stainless-steel",
    title: "Stainless Steel",
    totalDuration: "2-5 weeks",
    description: "Premium stainless steel fabrication for contemporary, corrosion-resistant metal solutions.",
    steps: [
      {
        title: "Project Requirements",
        duration: "1-2 days",
        description: "Discuss your stainless steel project needs, including grade selection based on environment and use."
      },
      {
        title: "Material Grade Selection",
        duration: "1-2 days",
        description: "Recommend the optimal stainless steel grade (304, 316, etc.) for your specific application."
      },
      {
        title: "Design Development",
        duration: "3-7 days",
        description: "Create precision designs for your stainless steel components or structures."
      },
      {
        title: "Material Procurement",
        duration: "3-10 days",
        description: "Source high-quality stainless steel sheets, tubes, or profiles from trusted suppliers."
      },
      {
        title: "Precision Cutting",
        duration: "2-5 days",
        description: "Use laser or water-jet cutting for exact dimensions with minimal material waste."
      },
      {
        title: "Forming & Fabrication",
        duration: "5-12 days",
        description: "Shape and assemble stainless steel components using specialized techniques to prevent contamination."
      },
      {
        title: "Welding & Finishing",
        duration: "3-7 days",
        description: "Perform TIG welding and apply professional finishes (brushed, mirror, etc.) as specified."
      },
      {
        title: "Quality Control",
        duration: "1-2 days",
        description: "Inspect all welds and surfaces to ensure they meet the highest standards for stainless steel work."
      },
      {
        title: "Delivery & Installation",
        duration: "1-3 days",
        description: "Carefully transport and install your finished stainless steel project."
      }
    ]
  },
  {
    id: "steel-fabrication",
    title: "Steel Fabrication",
    totalDuration: "3-6 weeks",
    description: "Custom steel fabrication for structural, architectural, and decorative applications.",
    steps: [
      {
        title: "Project Scope Definition",
        duration: "2-3 days",
        description: "Define your steel fabrication requirements, including structural needs and load considerations."
      },
      {
        title: "Engineering & Design",
        duration: "5-10 days",
        description: "Develop detailed fabrication plans and structural calculations for your steel project."
      },
      {
        title: "Material Specification",
        duration: "2-3 days",
        description: "Select appropriate steel grades and profiles based on engineering requirements."
      },
      {
        title: "Material Procurement",
        duration: "5-12 days",
        description: "Source required steel beams, plates, tubes, and connection hardware."
      },
      {
        title: "Cutting & Preparation",
        duration: "3-7 days",
        description: "Cut, drill, and prepare steel components according to fabrication drawings."
      },
      {
        title: "Assembly & Welding",
        duration: "7-14 days",
        description: "Assemble and weld steel components using industry-best practices for structural integrity."
      },
      {
        title: "Surface Treatment",
        duration: "3-5 days",
        description: "Apply appropriate treatments such as sandblasting, priming, painting, or galvanizing."
      },
      {
        title: "Quality Inspection",
        duration: "1-2 days",
        description: "Perform dimensional checks and weld inspections to verify compliance with specifications."
      },
      {
        title: "Transportation & Installation",
        duration: "2-5 days",
        description: "Safely transport and install fabricated steel structures at your project site."
      }
    ]
  }
];